import React, { useState } from 'react';
import { Card, Row, Col, Button, Form, Table, ProgressBar, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../../../services/AxiosInstance';
import Api from '../../../../constants/Api';
import PageTitle from '../../../layouts/PageTitle';
import { successAlert, errorAlert } from '../../../components/alert/defaultAlert';
import { useBranch } from '../../../../context/BranchContext';
import DashboardHeader from '../../../components/Common/DashboardHeader';
import './ImportData.css';

const ImportData = () => {
    const { t } = useTranslation();
    const { selectedBranch } = useBranch();
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [importResult, setImportResult] = useState(null);
    const [showInstructions, setShowInstructions] = useState(false);

    // Exemplo de dados CSV para mostrar ao usuário
    const csvExample = `"Tipo","Descrição","Categoria","Forma de pagamento","Data de Pagamento","Data de referência","Valor","Status"
"RECEBER","Venda de produtos","Receita de Vendas","Dinheiro","15/11/2025","15/11/2025","1500.00","Pago"
"PAGAR","Aluguel escritório","Despesas Fixas","Transferência","05/11/2025","05/11/2025","2000.00","Pendente"
"PAGAR","Compra matéria prima","Custos Variáveis","Cartão","10/11/2025","10/11/2025","800.50","Pago"`;

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Validar se é arquivo CSV
            if (!file.name.toLowerCase().endsWith('.csv')) {
                errorAlert('Por favor, selecione um arquivo CSV válido.');
                setSelectedFile(null);
                return;
            }

            // Validar tamanho (10MB máximo)
            const maxSize = 10 * 1024 * 1024; // 10MB
            if (file.size > maxSize) {
                errorAlert('Arquivo muito grande. Tamanho máximo: 10MB');
                setSelectedFile(null);
                return;
            }

            setSelectedFile(file);
            setImportResult(null); // Limpar resultado anterior
        }
    };

    const handleUpload = async () => {
        // Validar filial obrigatória do contexto
        if (!selectedBranch || !selectedBranch.id) {
            errorAlert('Nenhuma filial selecionada no contexto. Selecione uma filial primeiro.');
            return;
        }

        if (!selectedFile) {
            errorAlert('Selecione um arquivo CSV antes de fazer o upload.');
            return;
        }

        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('branchId', selectedBranch.id);

        setUploading(true);
        setUploadProgress(0);

        try {
            const response = await axiosInstance.post(`${Api.cash_flow.import}/balance`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
                onUploadProgress: (progressEvent) => {
                    const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setUploadProgress(progress);
                }
            });

            setImportResult(response.data);

            if (response.data.success) {
                successAlert(`Importação concluída com sucesso! ${response.data.successfulImports} registros importados.`);
            } else {
                errorAlert(`Importação concluída com erros. ${response.data.successfulImports} sucessos, ${response.data.failedImports} falhas.`);
            }

        } catch (error) {
            console.error('Erro ao fazer upload:', error);
            errorAlert('Erro ao fazer upload do arquivo. Tente novamente.');
        } finally {
            setUploading(false);
            setUploadProgress(0);
        }
    };

    const downloadExampleCSV = () => {
        const blob = new Blob([csvExample], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', 'exemplo_importacao.csv');
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const resetForm = () => {
        setSelectedFile(null);
        setImportResult(null);
        setUploadProgress(0);
        // Limpar input file
        const fileInput = document.getElementById('csvFileInput');
        if (fileInput) {
            fileInput.value = '';
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const branchLabel = selectedBranch
        ? (selectedBranch.name || selectedBranch.description)
        : 'Nenhuma filial selecionada no contexto';

    const resultCards = importResult ? [
        {
            key: 'total',
            label: 'Total de Linhas',
            value: importResult.totalRecords,
            tone: 'primary',
            icon: 'fa-table-list'
        },
        {
            key: 'success',
            label: 'Sucessos',
            value: importResult.successfulImports,
            tone: 'success',
            icon: 'fa-check-circle'
        },
        {
            key: 'failed',
            label: 'Falhas',
            value: importResult.failedImports,
            tone: 'danger',
            icon: 'fa-triangle-exclamation'
        },
        {
            key: 'categories',
            label: 'Categorias Criadas',
            value: importResult.categoriesCreated,
            tone: 'info',
            icon: 'fa-folder-plus'
        }
    ] : [];

    const successRate = importResult && importResult.totalRecords > 0
        ? Math.round(importResult.successfulImports / importResult.totalRecords * 100)
        : 0;

    return (
        <>
            <PageTitle
                activeMenu="Importar Dados"
                motherMenu={t('cash_flow.label')}
            />

            <div className="import-data-page">
                <DashboardHeader
                    className="import-data-header"
                    eyebrow={t('cash_flow.label')}
                    title="Importar Dados de Balanço"
                    subtitle="Envie um CSV padronizado para criar lançamentos em lote e atualizar categorias automaticamente na filial selecionada."
                    metrics={[{ key: 'branch', label: 'Filial', value: branchLabel, tone: selectedBranch ? 'info' : 'warning' }]}
                >
                    <div className="import-data-header__actions">
                                <Button variant="light" onClick={downloadExampleCSV}>
                                    <i className="fas fa-download me-2"></i>
                                    Exemplo CSV
                                </Button>
                                <Button variant="outline-light" onClick={() => setShowInstructions(true)}>
                                    <i className="fas fa-circle-info me-2"></i>
                                    Instruções
                                </Button>
                    </div>
                </DashboardHeader>

                <Card className="import-panel import-context-card border-0">
                    <Card.Body>
                        <div className={`import-branch-banner ${selectedBranch ? 'is-ready' : 'is-warning'}`}>
                            <i className={`fas ${selectedBranch ? 'fa-building' : 'fa-exclamation-triangle'} me-2`}></i>
                            <div>
                                <strong>Filial de Importação:</strong> {branchLabel}
                                {!selectedBranch && (
                                    <div className="mt-1">
                                        <small>Selecione uma filial no cabeçalho do sistema antes de fazer a importação.</small>
                                    </div>
                                )}
                            </div>
                        </div>
                    </Card.Body>
                </Card>

                <Row className="g-4 align-items-stretch">
                    <Col xl={8}>
                        <Card className="import-panel border-0 h-100">
                            <Card.Body>
                                <div className="import-section-heading">
                                    <div>
                                        <h3 className="import-section-title">Arquivo de importação</h3>
                                        <p className="import-section-subtitle mb-0">Selecione um CSV válido com até 10MB para iniciar a importação.</p>
                                    </div>
                                    <Button
                                        variant="outline-secondary"
                                        onClick={resetForm}
                                        disabled={uploading}
                                    >
                                        <i className="fas fa-times me-2"></i>
                                        Limpar
                                    </Button>
                                </div>

                                <Form.Group>
                                    <Form.Label>
                                        <i className="fas fa-file-csv me-2"></i>
                                        Selecionar Arquivo CSV
                                    </Form.Label>
                                    <Form.Control
                                        id="csvFileInput"
                                        type="file"
                                        accept=".csv"
                                        onChange={handleFileSelect}
                                        disabled={uploading}
                                    />
                                    <Form.Text className="text-muted">
                                        Apenas arquivos CSV com vírgulas e aspas duplas.
                                    </Form.Text>
                                </Form.Group>

                                {selectedFile ? (
                                    <div className="import-file-card">
                                        <div>
                                            <h6 className="mb-2">
                                                <i className="fas fa-file-csv text-success me-2"></i>
                                                {selectedFile.name}
                                            </h6>
                                            <p className="text-muted mb-0">
                                                Tamanho: {formatFileSize(selectedFile.size)} · Modificado: {new Date(selectedFile.lastModified).toLocaleString()}
                                            </p>
                                        </div>
                                        <Button
                                            variant="primary"
                                            onClick={handleUpload}
                                            disabled={uploading || !selectedBranch?.id}
                                            size="lg"
                                            title={!selectedBranch?.id ? 'Selecione uma filial no cabeçalho do sistema primeiro' : ''}
                                        >
                                            {uploading ? (
                                                <>
                                                    <div className="spinner-border spinner-border-sm me-2" role="status">
                                                        <span className="visually-hidden">Enviando...</span>
                                                    </div>
                                                    Importando...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fas fa-upload me-2"></i>
                                                    Importar Dados
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="import-dropzone-hint">
                                        <i className="fas fa-cloud-arrow-up"></i>
                                        <span>Selecione um arquivo para liberar a importação.</span>
                                    </div>
                                )}

                                {uploading && (
                                    <div className="mt-4">
                                        <div className="d-flex justify-content-between small text-muted mb-2">
                                            <span>Enviando arquivo</span>
                                            <strong>{uploadProgress}%</strong>
                                        </div>
                                        <ProgressBar
                                            now={uploadProgress}
                                            label={`${uploadProgress}%`}
                                            variant="info"
                                            animated
                                        />
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col xl={4}>
                        <Card className="import-panel border-0 h-100">
                            <Card.Body>
                                <div className="import-section-heading">
                                    <div>
                                        <h3 className="import-section-title">Checklist rápido</h3>
                                        <p className="import-section-subtitle mb-0">Antes de enviar, confirme o básico para evitar rejeições.</p>
                                    </div>
                                </div>

                                <div className="import-checklist">
                                    <div className="import-checklist-item">
                                        <i className="fas fa-check-circle"></i>
                                        <span>CSV com colunas na ordem esperada.</span>
                                    </div>
                                    <div className="import-checklist-item">
                                        <i className="fas fa-check-circle"></i>
                                        <span>Arquivo com até 10MB.</span>
                                    </div>
                                    <div className="import-checklist-item">
                                        <i className="fas fa-check-circle"></i>
                                        <span>Filial selecionada no contexto.</span>
                                    </div>
                                    <div className="import-checklist-item">
                                        <i className="fas fa-check-circle"></i>
                                        <span>Codificação UTF-8 e cabeçalho na primeira linha.</span>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {importResult && (
                    <>
                        <Row className="g-3">
                            {resultCards.map((card) => (
                                <Col xl={3} md={6} key={card.key}>
                                    <Card className={`import-metric-card import-metric-card--${card.tone} border-0`}>
                                        <Card.Body>
                                            <div className="import-metric-icon">
                                                <i className={`fas ${card.icon}`}></i>
                                            </div>
                                            <span className="import-metric-label">{card.label}</span>
                                            <strong className="import-metric-value">{card.value}</strong>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            ))}
                        </Row>

                        <Card className="import-panel border-0">
                            <Card.Body>
                                <div className="import-section-heading import-section-heading--results">
                                    <div>
                                        <h3 className="import-section-title">Resultado da Importação</h3>
                                        <p className="import-section-subtitle mb-0">
                                            {importResult.success ? 'Processo concluído com sucesso.' : 'Processo concluído com atenção para falhas e avisos.'}
                                        </p>
                                    </div>
                                    <div className={`import-result-badge ${importResult.success ? 'is-success' : 'is-warning'}`}>
                                        <i className={`fas ${importResult.success ? 'fa-check-circle' : 'fa-triangle-exclamation'} me-2`}></i>
                                        {successRate}% de sucesso
                                    </div>
                                </div>

                                {importResult.errors && importResult.errors.length > 0 && (
                                    <div className="import-feedback-panel is-danger mb-3">
                                        <h6 className="mb-2">
                                            <i className="fas fa-triangle-exclamation me-2"></i>
                                            Erros Encontrados
                                        </h6>
                                        <div className="import-feedback-list">
                                            {importResult.errors.map((error, index) => (
                                                <div key={index} className="import-feedback-item text-danger">
                                                    <i className="fas fa-times-circle me-1"></i>
                                                    {error}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {importResult.warnings && importResult.warnings.length > 0 && (
                                    <div className="import-feedback-panel is-warning mb-3">
                                        <h6 className="mb-2">
                                            <i className="fas fa-circle-exclamation me-2"></i>
                                            Avisos
                                        </h6>
                                        <div className="import-feedback-list">
                                            {importResult.warnings.map((warning, index) => (
                                                <div key={index} className="import-feedback-item text-warning-emphasis">
                                                    <i className="fas fa-exclamation-triangle me-1"></i>
                                                    {warning}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </>
                )}
            </div>

            {/* Modal de Instruções */}
            <Modal show={showInstructions} onHide={() => setShowInstructions(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="fas fa-info-circle me-2"></i>
                        Instruções de Importação
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-4">
                        <h5 className="text-primary">📋 Formato do Arquivo CSV</h5>
                        <p>O arquivo deve ser um CSV com separador vírgula (,) e campos entre aspas, contendo as seguintes colunas:</p>

                        <Table bordered size="sm" className="mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th>Ordem</th>
                                    <th>Coluna</th>
                                    <th>Obrigatória</th>
                                    <th>Descrição</th>
                                    <th>Exemplo</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>1</td>
                                    <td>Tipo</td>
                                    <td><span className="badge bg-danger">Sim</span></td>
                                    <td>PAGAR, RECEBER, PAYABLE, RECEIVABLE</td>
                                    <td>RECEBER</td>
                                </tr>
                                <tr>
                                    <td>2</td>
                                    <td>Descrição</td>
                                    <td><span className="badge bg-danger">Sim</span></td>
                                    <td>Descrição do lançamento</td>
                                    <td>Venda de produtos</td>
                                </tr>
                                <tr>
                                    <td>3</td>
                                    <td>Categoria</td>
                                    <td><span className="badge bg-danger">Sim</span></td>
                                    <td>Nome da categoria</td>
                                    <td>Receita de Vendas</td>
                                </tr>
                                <tr>
                                    <td>4</td>
                                    <td>Forma de pagamento</td>
                                    <td><span className="badge bg-secondary">Não</span></td>
                                    <td>Método de pagamento</td>
                                    <td>Dinheiro</td>
                                </tr>
                                <tr>
                                    <td>5</td>
                                    <td>Data de vencimento</td>
                                    <td><span className="badge bg-danger">Sim</span></td>
                                    <td>dd/MM/yyyy, yyyy-MM-dd ou dd-MM-yyyy</td>
                                    <td>15/11/2025</td>
                                </tr>
                                <tr>
                                    <td>6</td>
                                    <td>Data de referência</td>
                                    <td><span className="badge bg-secondary">Não</span></td>
                                    <td>Data de referência do lançamento</td>
                                    <td>15/11/2025</td>
                                </tr>
                                <tr>
                                    <td>7</td>
                                    <td>Valor</td>
                                    <td><span className="badge bg-danger">Sim</span></td>
                                    <td>Valor monetário (decimal com . ou ,)</td>
                                    <td>1500.00</td>
                                </tr>
                                <tr>
                                    <td>8</td>
                                    <td>Status</td>
                                    <td><span className="badge bg-secondary">Não</span></td>
                                    <td>Pago, Pendente, Cancelado</td>
                                    <td>Pago</td>
                                </tr>
                            </tbody>
                        </Table>
                    </div>

                    <div className="mb-4">
                        <h5 className="text-warning">⚠️ Regras Importantes</h5>
                        <ul>
                            <li><strong>Separador:</strong> Use vírgula (,) entre as colunas e campos entre aspas duplas</li>
                            <li><strong>Ordem das colunas:</strong> Deve seguir exatamente a ordem especificada na tabela acima</li>
                            <li><strong>Categorias:</strong> Se a categoria não existir, será criada automaticamente</li>
                            <li><strong>Tipos aceitos:</strong> PAGAR, PAGAMENTO, DÉBITO → PAYABLE | RECEBER, RECEBIMENTO, CRÉDITO → RECEIVABLE</li>
                            <li><strong>Valores:</strong> Use ponto (.) ou vírgula (,) como separador decimal</li>
                            <li><strong>Status aceitos:</strong> Pago, Pendente, Cancelado (se vazio, será definido como Pendente)</li>
                            <li><strong>Filial:</strong> Os dados serão importados para a filial selecionada no contexto do sistema</li>
                        </ul>
                    </div>

                    <div className="mb-3">
                        <h5 className="text-success">✅ Dicas de Sucesso</h5>
                        <ul>
                            <li>Teste com um arquivo pequeno primeiro</li>
                            <li>Use o arquivo de exemplo como base</li>
                            <li>Verifique a codificação do arquivo (UTF-8 recomendado)</li>
                            <li>Remova linhas em branco no final do arquivo</li>
                            <li>Mantenha o cabeçalho na primeira linha</li>
                        </ul>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="success" onClick={downloadExampleCSV}>
                        <i className="fas fa-download me-1"></i>
                        Baixar Exemplo
                    </Button>
                    <Button variant="secondary" onClick={() => setShowInstructions(false)}>
                        Fechar
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default ImportData;
